import React, { useEffect, useState } from 'react';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import NavBar from '../../../../component/b2c/navBar/navBar';
import Pagination from '@mui/material/Pagination';
import { useMyContext } from '../../../../context/MyContext';
import { FaCcVisa, FaCcMastercard, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFilter, FaTimes, FaCheckCircle } from "react-icons/fa";
import PaginationItem from '@mui/material/PaginationItem';
import { Base_Url, topUpList, userTrnxList } from '../../../../config/config';

const TopupHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const cus_id = localStorage.getItem('cus_id');
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [historyData, setHistoryData] = useState([]);
  const [allHistoryData, setAllHistoryData] = useState([]); // Store all data for client-side filtering
  const navigate = useNavigate();

  // Add date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const handleNavigation = () => {
    navigate('/dashboard');
  };

  const getTopupHistory = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=${page}&limit=${limit}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === 'SUCCESS') {
        const transactions = data.data;

        if (isDateFilterActive) {
          // Apply client-side filtering based on dates
          const filtered = applyDateFilter(transactions);
          setHistoryData(filtered);
          setTotalOrders(filtered.length);
        } else {
          setHistoryData(transactions);
          setTotalOrders(data.pagination.totalItems || 0);
        }

        // Store all data for filtering
        setAllHistoryData(prev => {
          // Combine with previous data to build complete dataset
          const combinedData = [...prev];

          // Add new transactions if they don't already exist
          transactions.forEach(transaction => {
            if (!combinedData.some(item => item.id === transaction.id)) {
              combinedData.push(transaction);
            }
          });

          return combinedData;
        });
      } else {
        toast.error(`Error: ${data.message}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to fetch all data for export
  const fetchAllData = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=1&limit=1000`; // Large limit to get as much data as possible

    try {
      toast.loading('Preparing data...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      toast.dismiss();

      if (data.status === 'SUCCESS') {
        const allData = data.data;
        return isDateFilterActive ? applyDateFilter(allData) : allData;
      } else {
        toast.error(`Error: ${data.message}`);
        return [];
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error: ${error.message}`);
      return [];
    }
  };

  useEffect(() => {
    getTopupHistory();
  }, [page, limit]);

  useEffect(() => {
    if (isDateFilterActive) {
      // Apply date filter to all data
      const filtered = applyDateFilter(allHistoryData);
      setFilteredData(filtered);

      // Update pagination with filtered data
      const paginatedData = filtered.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
      setTotalOrders(filtered.length);
    } else {
      // Reset to normal API-based pagination
      getTopupHistory();
    }
  }, [isDateFilterActive, startDate, endDate]);

  useEffect(() => {
    if (isDateFilterActive) {
      // When page or limit changes with active filter, update displayed data
      const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
    }
  }, [page, limit, filteredData]);

  const applyDateFilter = (data) => {
    if (!startDate || !endDate) return data;

    const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
    const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

    return data.filter(item => {
      const txnDate = new Date(item.txn_date).getTime();
      return txnDate >= startTimestamp && txnDate <= endTimestamp;
    });
  };

  const handleApplyDateFilter = () => {
    if (!startDate || !endDate) {
      toast.warning('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning('Start date cannot be after end date');
      return;
    }

    setIsDateFilterActive(true);
    setPage(1); // Reset to first page when filter changes
  };

  const handleResetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsDateFilterActive(false);
    setPage(1);
  };

  // Function to convert JSON to CSV
  const convertToCSV = (data) => {
    const headers = role === 'admin'
      ? ['User ID', 'Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date']
      : ['Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date'];

    // Create CSV header row
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = role === 'admin'
        ? [
          `"${row.user_id || 'N/A'}"`,
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ]
        : [
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ];

      csvContent += values.join(',') + '\n';
    });

    return csvContent;
  };

  const downloadTopupHistory = async () => {
    try {
      toast.loading('Preparing download...');

      // Get all data for CSV export
      const dataToExport = await fetchAllData();

      if (dataToExport.length === 0) {
        toast.dismiss();
        toast.error('No data available to download');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(dataToExport);

      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `topup-history-${startDate || 'all'}-to-${endDate || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Download successful');
    } catch (error) {
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const renderTableHeaders = () => {
    const headers = [
      'Transaction ID',
      'Amount',
      'Last Balance',
      'Transaction Type',
      'Transaction Status',
      'Transaction Date',
      'Description',
    ];
    if (role === 'admin') headers.unshift('User ID');
    return headers;
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
    setPage(1);
  };

  const renderTableRow = (row, index) => (
    <tr
      key={row.id}
      className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
    >
      {role === 'admin' && (
        <td className="px-3 py-2 text-xs text-black dark:text-white font-medium whitespace-nowrap">
          {row.user_id}
        </td>
      )}
      <td className="relative group px-3 py-2 text-xs text-black dark:text-white font-medium cursor-pointer whitespace-nowrap">
        {row.trnx_id || "N/A"}
        <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-black text-white text-[10px] rounded px-2 py-1 shadow-lg z-10 whitespace-nowrap">
          View transaction details
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-black dark:text-white font-semibold whitespace-nowrap">
        ₹{row.credit_amount || "N/A"}
      </td>
      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">
        ₹{row.closing_balance || "N/A"}
      </td>
      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 capitalize whitespace-nowrap">
        {row.payment_mode || "N/A"}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {row.status === 'add' ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-black text-black dark:border-white dark:text-white">
            Success
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border border-gray-400 text-gray-600 dark:border-gray-500 dark:text-gray-400">
            {row.status}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">
        {row.txn_date || "N/A"}
      </td>
      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300 max-w-[180px]">
        <div
          className="truncate hover:text-clip hover:whitespace-normal cursor-help"
          title={row.txn_description || "No description available"}
        >
          {row.txn_description || "N/A"}
        </div>
      </td>
    </tr>
  );

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-100 dark:bg-black dark:text-white md:bg-transparent mt-3 md:w-64 md:z-20"
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}

        {/* Main panel: fixed height, no page scroll */}
        <div className="w-full flex flex-col overflow-hidden dark:bg-black dark:text-white border border-gray-200 dark:border-gray-800 bg-gray-200 p-3">

          {/* Header button */}
          <button
            onClick={handleNavigation}
            className="shrink-0 text-sm sm:text-base font-semibold bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-white rounded h-9 w-full sm:w-auto sm:px-6 flex items-center justify-center gap-2"
          >
            <FaCcVisa size={16} />
            <FaCcMastercard size={16} />
            TopUp History
          </button>

          {/* Filter bar - compact */}
          <div className="shrink-0 mt-3 p-3 border border-gray-200 dark:border-gray-800 rounded-md bg-gray-50 dark:bg-gray-950">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-3">
              <div className="flex flex-col w-full md:w-auto">
                <label className="text-[11px] font-medium mb-1 text-gray-600 dark:text-gray-400">Start Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border border-gray-300 dark:border-gray-700 rounded p-2 pr-8 w-full md:w-40 text-xs bg-white dark:bg-black dark:text-gray-200 focus:ring-1 focus:ring-black dark:focus:ring-white focus:outline-none"
                  />
                  <FaCalendarAlt size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="flex flex-col w-full md:w-auto">
                <label className="text-[11px] font-medium mb-1 text-gray-600 dark:text-gray-400">End Date</label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border border-gray-300 dark:border-gray-700 rounded p-2 pr-8 w-full md:w-40 text-xs bg-white dark:bg-black dark:text-gray-200 focus:ring-1 focus:ring-black dark:focus:ring-white focus:outline-none"
                  />
                  <FaCalendarAlt size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                <button
                  onClick={handleApplyDateFilter}
                  className="bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 text-white font-medium rounded px-3 py-2 text-xs flex items-center justify-center gap-1.5 flex-grow md:flex-grow-0"
                >
                  <FaFilter size={11} />
                  Apply
                </button>

                {isDateFilterActive && (
                  <button
                    onClick={handleResetDateFilter}
                    className="bg-white dark:bg-black border border-gray-300 dark:border-gray-700 text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-900 font-medium rounded px-3 py-2 text-xs flex items-center justify-center gap-1.5 flex-grow md:flex-grow-0"
                  >
                    <FaTimes size={11} />
                    Reset
                  </button>
                )}

                <button
                  onClick={downloadTopupHistory}
                  className="bg-white dark:bg-black border border-black dark:border-white text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-900 font-medium rounded px-3 py-2 text-xs flex items-center justify-center gap-1.5 flex-grow md:flex-grow-0"
                >
                  <FaDownload size={11} />
                  Download
                </button>
              </div>
            </div>

            {isDateFilterActive && (
              <div className="mt-2 text-[11px] font-medium flex items-center gap-1.5 text-black dark:text-white">
                <FaCheckCircle size={11} />
                Showing {new Date(startDate).toLocaleDateString()} – {new Date(endDate).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Table - ONLY this scrolls, screen doesn't */}
          <div className="flex-1 min-h-0 mt-3 border border-gray-200 dark:border-gray-800 rounded-md overflow-auto">
            <table className="min-w-full table-auto text-left">
              <thead className="sticky top-0 z-[1] bg-black text-white dark:bg-white dark:text-black text-[10px] uppercase tracking-wide">
                <tr>
                  {renderTableHeaders().map((header, index) => (
                    <th key={index} className="px-3 py-2 font-medium whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="dark:bg-black dark:text-white bg-white">
                {historyData?.length > 0 ? (
                  historyData.map((row, index) => renderTableRow(row, index))
                ) : (
                  <tr>
                    <td colSpan={role === 'admin' ? "8" : "7"} className="text-center p-6 text-gray-500 dark:text-gray-400 text-xs">
                      {isDateFilterActive ? 'No data available for the selected date range' : 'No data available'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer / pagination */}
          <div className="shrink-0 mt-3 flex flex-col sm:flex-row justify-between items-center gap-2">
            <span className="text-xs text-gray-700 dark:text-gray-300 font-semibold">{`Total : ${totalOrders}`}</span>

            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-xs bg-white dark:bg-black dark:text-gray-200 focus:outline-none"
            >
              {totalOrders === 0 ? (
                <option value={0}>0</option>
              ) : (
                <>
                  {totalOrders >= 10 && <option value={10}>10</option>}
                  {totalOrders >= 15 && <option value={15}>15</option>}
                  {totalOrders >= 20 && <option value={20}>20</option>}
                  {totalOrders >= 30 && <option value={30}>30</option>}
                  {totalOrders >= 30 && <option value={totalOrders}>All</option>}
                </>
              )}
            </select>

            <Pagination
              count={Math.ceil(totalOrders / limit)}
              page={page}
              onChange={handlePaginationChange}
              variant="outlined"
              shape="rounded"
              size="small"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={`mx-0.5 rounded ${item.selected ? 'bg-black text-white dark:bg-white dark:text-black' : 'text-gray-700 dark:text-gray-300'} hover:bg-gray-800 hover:text-white`}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default TopupHistory;